// Loads & registers maps for ECharts (world + drill-down country maps) and computes centroids.
import * as echarts from "echarts";
import api from "@/lib/api";

// Countries that have a bundled state/province map for geographical drill-down
// (items 45-47: India, UAE, Kenya, Tanzania, Nigeria, Singapore, Malaysia, Indonesia, Thailand, USA).
const M = (name, file) => ({ name, file });
const COUNTRY_MAPS = {
  "United States": M("USA", "/maps/usa.json"),
  "United States of America": M("USA", "/maps/usa.json"),
  "USA": M("USA", "/maps/usa.json"),
  "US": M("USA", "/maps/usa.json"),
  "India": M("India", "/maps/india.json"),
  "Republic of India": M("India", "/maps/india.json"),
  "United Arab Emirates": M("UAE", "/maps/uae.json"),
  "UAE": M("UAE", "/maps/uae.json"),
  "Kenya": M("Kenya", "/maps/kenya.json"),
  "Tanzania": M("Tanzania", "/maps/tanzania.json"),
  "United Republic of Tanzania": M("Tanzania", "/maps/tanzania.json"),
  "Nigeria": M("Nigeria", "/maps/nigeria.json"),
  "Singapore": M("Singapore", "/maps/singapore.json"),
  "Malaysia": M("Malaysia", "/maps/malaysia.json"),
  "Indonesia": M("Indonesia", "/maps/indonesia.json"),
  "Thailand": M("Thailand", "/maps/thailand.json"),
};

// Item 45: Shape Kind choices for map widgets — world countries or a country's states.
export const BUILTIN_STATE_MAPS = [
  { key: "India", label: "India — states" },
  { key: "UAE", label: "United Arab Emirates — emirates" },
  { key: "Kenya", label: "Kenya — counties" },
  { key: "Tanzania", label: "Tanzania — regions" },
  { key: "Nigeria", label: "Nigeria — states" },
  { key: "Singapore", label: "Singapore — regions" },
  { key: "Malaysia", label: "Malaysia — states" },
  { key: "Indonesia", label: "Indonesia — provinces" },
  { key: "Thailand", label: "Thailand — provinces" },
  { key: "USA", label: "United States — states" },
];

const MAP_BY_KEY = { USA: M("USA", "/maps/usa.json") };
BUILTIN_STATE_MAPS.forEach(({ key }) => {
  if (!MAP_BY_KEY[key]) MAP_BY_KEY[key] = M(key, `/maps/${key.toLowerCase()}.json`);
});

// Item 46: automatic geographic field detection by name patterns.
const GEO_PATTERNS = [
  { role: "lat", re: /^(lat|latitude|y_coord|geo_lat)$/i },
  { role: "lng", re: /^(lon|lng|long|longitude|x_coord|geo_lon|geo_lng)$/i },
  { role: "country", re: /(country|nation)/i },
  { role: "state", re: /(state|province|region|county|emirate|prefecture)/i },
  { role: "city", re: /(city|town|municipality|locality)/i },
  { role: "postal", re: /(zip|postal|pincode|postcode)/i },
];
export function detectGeoFields(fields) {
  const out = {};
  for (const f of fields || []) {
    const n = typeof f === "string" ? f : f.name;
    for (const { role, re } of GEO_PATTERNS) {
      if (!out[role] && re.test(n)) { out[role] = n; break; }
    }
  }
  return out;
}

const registered = {};            // mapName -> true
const centroidsByMap = {};        // mapName -> { regionName: [lng, lat] }
const promises = {};              // mapName -> Promise

function bboxOfCoords(coords, box) {
  if (typeof coords[0] === "number") {
    const [lng, lat] = coords;
    box[0] = Math.min(box[0], lng); box[1] = Math.min(box[1], lat);
    box[2] = Math.max(box[2], lng); box[3] = Math.max(box[3], lat);
    return;
  }
  for (const c of coords) bboxOfCoords(c, box);
}

function computeCentroids(geo, mapName) {
  const out = {};
  for (const f of geo.features) {
    const name = f.properties && (f.properties.name || f.properties.NAME);
    if (!name || !f.geometry) continue;
    const box = [Infinity, Infinity, -Infinity, -Infinity];
    try { bboxOfCoords(f.geometry.coordinates, box); } catch (e) { continue; }
    if (box[0] !== Infinity) out[name] = [(box[0] + box[2]) / 2, (box[1] + box[3]) / 2];
  }
  centroidsByMap[mapName] = out;
}

function loadMap(mapName, file) {
  if (registered[mapName]) return Promise.resolve(mapName);
  if (promises[mapName]) return promises[mapName];
  promises[mapName] = fetch(`${process.env.PUBLIC_URL || ""}${file}`)
    .then((r) => r.json())
    .then((geo) => {
      echarts.registerMap(mapName, geo);
      registered[mapName] = true;
      computeCentroids(geo, mapName);
      return mapName;
    })
    .catch(() => null);
  return promises[mapName];
}

export function ensureWorldMap() {
  return loadMap("world", "/maps/world.json");
}

export function getCentroids(mapName = "world") {
  return centroidsByMap[mapName] || {};
}

// Region names known to a registered map (for unmatched-value detection)
export function getRegionNames(mapName = "world") {
  return Object.keys(centroidsByMap[mapName] || {});
}

// Items 11/12: dataset values rarely carry the map's exact spelling (e.g. "Maharashtra" vs
// "Mahārāshtra", "Tamil Nadu" vs "Tamil Nādu", "NCT of Delhi" vs "Delhi"). Region names are
// therefore matched on a diacritic / case / punctuation-insensitive key before being reported
// as unmatched, so choropleths, bubbles and the state → city drill all join correctly.
const regionIndexByMap = {};
const regionKey = (s) => String(s || "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/\band\b|&/g, " ")
  .replace(/\b(state|province|district|region|union territory|ut|nct|of|the)\b/g, " ")
  .replace(/[^a-z0-9]+/g, "");

function regionIndex(mapName) {
  if (regionIndexByMap[mapName]) return regionIndexByMap[mapName];
  const idx = {};
  Object.keys(centroidsByMap[mapName] || {}).forEach((n) => {
    const k = regionKey(n);
    if (k && !(k in idx)) idx[k] = n;
  });
  regionIndexByMap[mapName] = idx;
  return idx;
}

// Returns the map's own spelling for a data value, or null when the map has no such region.
export function resolveRegionName(mapName, value) {
  if (value == null || value === "") return null;
  const names = centroidsByMap[mapName];
  if (!names) return null;
  const raw = String(value);
  if (raw in names) return raw;
  return regionIndex(mapName)[regionKey(raw)] || null;
}

// Normalised key used to match a clicked region back to the raw dataset value (drill-down).
export const regionMatchKey = regionKey;

// Item 45: built-in city centroid lookup (static /maps/cities.json) — fallback when a dataset has no lat/lon.
let cityIndex = null;          // normalised name -> [lng, lat, countryCode]
let cityPromise = null;
const normName = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
export function ensureCityCentroids() {
  if (cityIndex) return Promise.resolve(cityIndex);
  if (cityPromise) return cityPromise;
  cityPromise = fetch(`${process.env.PUBLIC_URL || ""}/maps/cities.json`)
    .then((r) => r.json())
    .then((j) => {
      const idx = {};
      Object.entries(j.cities || j).forEach(([name, v]) => { idx[normName(name)] = v; });
      cityIndex = idx;
      return idx;
    })
    .catch(() => { cityIndex = {}; return cityIndex; });
  return cityPromise;
}
export function citiesReady() { return !!cityIndex; }
// Returns [lng, lat] or null. Accepts "City", "City, Country" and "City (ST)" forms.
export function lookupCity(name) {
  if (!cityIndex) return null;
  const raw = String(name || "");
  const cands = [raw, raw.split(",")[0], raw.replace(/\s*\(.*\)\s*$/, "")];
  for (const c of cands) {
    const hit = cityIndex[normName(c)];
    if (hit) return [hit[0], hit[1]];
  }
  return null;
}

export function countryMapInfo(countryName) {
  if (countryName == null || countryName === "") return null;
  const raw = String(countryName);
  const direct = COUNTRY_MAPS[raw] || MAP_BY_KEY[raw];
  if (direct) return direct;
  // dataset spellings rarely match exactly ("india", "U.A.E.", "United-States")
  if (!countryMapInfo._idx) {
    const idx = {};
    [COUNTRY_MAPS, MAP_BY_KEY].forEach((src) => {
      Object.entries(src).forEach(([k, v]) => { const n = regionKey(k); if (n && !(n in idx)) idx[n] = v; });
    });
    countryMapInfo._idx = idx;
  }
  return countryMapInfo._idx[regionKey(raw)] || null;
}

// Returns the registered map name for a country, or null if unsupported.
export async function ensureCountryMap(countryName) {
  const info = countryMapInfo(countryName);
  if (!info) return null;
  return loadMap(info.name, info.file);
}

// Custom uploaded GeoJSON maps (sales territories etc.) served by the backend.
export async function ensureCustomMap(mapId, publicToken) {
  const name = `custom_${mapId}`;
  if (registered[name]) return name;
  if (promises[name]) return promises[name];
  const url = publicToken
    ? `/geo-maps/public/${mapId}/geojson?token=${encodeURIComponent(publicToken)}`
    : `/geo-maps/${mapId}/geojson`;
  promises[name] = api.get(url, { timeout: 60000 })
    .then(({ data }) => {
      echarts.registerMap(name, data);
      registered[name] = true;
      computeCentroids(data, name);
      return name;
    })
    .catch(() => { promises[name] = null; return null; });
  return promises[name];
}
